import { Spiner } from "@components/loaders/spiner";
import { StudentTahfidz } from "@services/dtos/school.dto";
import { SchoolService } from "@services/school.service";
import { useThemeStore } from "@stores/theme.store";
import { useQuery } from "@tanstack/react-query";
import { linkPage } from "@utils/router";
import { useEffect, useState } from "react";
import { FaAngleRight, FaChevronDown } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { MdImportContacts } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const jenisBadge: Record<string, { label: string; color: string }> = {
  sabaq: { label: "Sabaq", color: "bg-blue-dark" },
  sabqi: { label: "Sabqi", color: "bg-teal-dark" },
  manzil: { label: "Manzil", color: "bg-orange-dark" },
};

const getJenisBadge = (jenis: string) =>
  jenisBadge[jenis?.toLowerCase()] ?? {
    label: jenis ?? "-",
    color: "bg-blue-dark",
  };

const absenBadge: Record<string, { label: string; color: string }> = {
  hadir: { label: "HADIR", color: "bg-blue-dark" },
  sakit: { label: "SAKIT", color: "bg-red-dark" },
  izin: { label: "IZIN", color: "bg-blue-dark" },
  ijin: { label: "IZIN", color: "bg-blue-dark" },
  alpa: { label: "ALPHA", color: "bg-magenta-dark" },
  alpha: { label: "ALPHA", color: "bg-magenta-dark" },
};

const getAbsenBadge = (absen: string) =>
  absenBadge[absen?.toLowerCase()] ?? {
    label: absen?.toUpperCase() ?? "-",
    color: "bg-blue-dark",
  };

export const StudentTahfidzPage = () => {
  const navigate = useNavigate();
  const [limit, setLimit] = useState<string>("10");
  const { setFooter, setHeader, setAppBar } = useThemeStore((state) => state);

  const { data, isLoading } = useQuery(
    [SchoolService.queries.GET_SCHOOL_STUDENT_TAHFIDZ, limit],
    () => SchoolService.studentTahfidz(limit)
  );

  useEffect(() => {
    setFooter(false);
    setHeader({
      type: "small",
      title: "Tahfidz Santri",
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ title: "Tahfidz Santri" });
  }, [setFooter, setHeader, setAppBar]);

  const onOpenDetail = (tahfidz: StudentTahfidz) => {
    navigate(
      {
        pathname: linkPage.SCHOOL_STUDENT_TAHFIDZ_DETAIL,
        search: `?id=${tahfidz.id}`,
      },
      { state: tahfidz }
    );
  };

  return (
    <div className="card card-style">
      <div className="content mb-0">
        <p className="mb-3">
          Data yang ditampilkan hanya {limit} data terakhir
        </p>

        <div className="input-style has-borders input-style-always-active no-icon mb-4">
          <label htmlFor="form5" className="color-highlight font-500">
            Filter Jumlah ditampilkan
          </label>
          <select
            defaultValue="10"
            onChange={(e) => setLimit(e.target.value)}
            className="bg-theme"
          >
            <option value="default" disabled>
              Filter Jumlah ditampilkan
            </option>
            <option value="10">10 Data Terakhir</option>
            <option value="25">25 Data Terakhir</option>
            <option value="50">50 Data Terakhir</option>
            <option value="100">100 Data Terakhir</option>
          </select>
          <span>
            <i>
              <FaChevronDown />
            </i>
          </span>
        </div>

        {isLoading ? (
          <div className="d-flex p-3">
            <Spiner className="!tw-text-gray-200 tw-fill-gray-500 dark:tw-fill-white dark:!tw-text-gray-600 tw-w-8 tw-h-8 m-auto " />
          </div>
        ) : data?.data != null && data.data.length > 0 ? (
          <div className="list-group list-group-flush">
            {data.data.map((item, idx) => (
              <div
                key={idx}
                className="list-group-item tw-bg-inherit d-flex tw-px-0 tw-py-3 tw-border-[rgba(0,0,0,0.05)] dark:tw-border-[rgba(255,255,255,0.05)] tw-cursor-pointer"
                onClick={() => onOpenDetail(item)}
              >
                <div className="rounded-sm tw-w-8 tw-h-8 d-flex tw-flex-none my-auto bg-blue-dark">
                  <MdImportContacts className="m-auto" size={18} />
                </div>
                <div className="tw-grow mx-3 my-auto d-flex flex-column tw-w-[calc(100vw-140px)]">
                  <div className="mb-1">
                    <span
                      className={`badge ${
                        getJenisBadge(item.jenis).color
                      } color-white font-10 tw-py-1 px-2 rounded-2 me-2`}
                    >
                      {getJenisBadge(item.jenis).label}
                    </span>
                    {item.absen?.toLowerCase() !== "hadir" && (
                      <span
                        className={`badge ${
                          getAbsenBadge(item.absen).color
                        } color-white font-10 tw-py-1 px-2 rounded-2`}
                      >
                        {getAbsenBadge(item.absen).label}
                      </span>
                    )}
                  </div>
                  <span className="tw-font-medium tw-text-sm dark:tw-text-white tw-truncate">
                    {item.surat} : Ayat {item.ayatMulai}-{item.ayatSelesai}
                    {item.juz != null ? ` · Juz ${item.juz}` : ""}
                  </span>
                  <strong className="tw-font-medium tw-text-[11px] tw-leading-4 tw-text-[#adb5bd] tw-truncate">
                    Nilai {item.nilai ?? "-"} · {item.catatan ?? "-"}
                  </strong>
                </div>
                <div className="my-auto">
                  <FaAngleRight
                    className="dark:tw-text-white tw-text-gray-500"
                    size={10}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tw-p-8 text-center ">
            <FcFinePrint size={50} />
            <div>Data tidak ditemukan</div>
          </div>
        )}
      </div>
    </div>
  );
};
